import { createPortal } from 'react-dom';
import { FileUpIcon } from 'lucide-react';
import { getChatDropHintLines } from '../chat-attachment-feedback';

type ChatDropOverlayProps = {
  audioEnabled: boolean;
  isActive: boolean;
};

/**
 * Full-screen drop zone shown while files are dragged over the window.
 * Motion follows the transitions.dev modal recipe (scale 0.96 -> 1 + fade, smooth-out
 * ease), kept under 200 ms and disabled with `prefers-reduced-motion`.
 * Purely decorative: the "+" button remains the keyboard path, and a polite live region
 * announces the drop target to assistive technologies.
 */
export function ChatDropOverlay({ audioEnabled, isActive }: ChatDropOverlayProps) {
  const state = isActive ? 'open' : 'closed';

  return createPortal(
    <>
      <div className="sr-only" role="status">
        {isActive ? 'Déposez pour joindre' : ''}
      </div>
      <div
        aria-hidden="true"
        className="group pointer-events-none invisible fixed inset-0 z-[60] flex items-center justify-center bg-background/70 p-4 opacity-0 backdrop-blur-sm transition-[opacity,visibility] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] data-[state=open]:visible data-[state=open]:opacity-100 data-[state=open]:duration-200 motion-reduce:transition-none dark:bg-background/60"
        data-state={state}
        data-testid="chat-drop-overlay"
      >
        <div className="flex size-full items-center justify-center rounded-3xl border-2 border-dashed border-foreground/15 px-6">
          <div className="flex max-w-2xl scale-[0.96] flex-col items-center gap-3 text-center opacity-0 transition-[transform,opacity] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] group-data-[state=open]:scale-100 group-data-[state=open]:opacity-100 group-data-[state=open]:duration-200 motion-reduce:scale-100 motion-reduce:transition-none">
            <FileUpIcon className="size-10 text-foreground/70" strokeWidth={1.5} />
            <p className="text-3xl font-semibold tracking-tight text-foreground">Déposez vos fichiers</p>
            <div className="flex flex-col gap-0.5 text-sm text-muted-foreground">
              {getChatDropHintLines(audioEnabled).map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
